import { useMemo } from 'react';
import { format } from 'date-fns';
import { ChevronRight, Phone } from 'lucide-react';
import { fetchCalls } from '../../api';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import { formatShortDate } from '../calls/callDetailFormat';
import useNodeRequest from '../nodes/useNodeRequest';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ErrorText, MutedText, SELECT_CLASS } from './callTypesControls';
import { callsNoun, formatCount, hasOperator } from './callTypesModel';

// Одна страница, а не весь срез: строка журнала тяжёлая (расшифровка, сводка), а полный
// список открывается ссылкой в журнале.
const CALLS_PAGE_SIZE = 50;

const callScoreLabel = (score) =>
  score === null || score === undefined || !Number.isFinite(Number(score))
    ? 'Без оценки'
    : `Балл ${Number(score).toFixed(1).replace('.', ',')}`;

/** Звонки выбранной пары: первая страница здесь, полный список — в журнале. */
const PairCallsCard = ({ dateRange, callType, nodeFilter, employees, selectedEmployeeId, onSelectEmployee, onOpenCallJournal }) => {
  const panels = usePanelSettings();
  const panelId = PANEL_IDS['ui:call-paths.calls'];
  const selectable = employees.filter((employee) => hasOperator(employee) && employee.calls > 0);
  // После смены периода или направления выбранного сотрудника может не быть в разборе: селект
  // показал бы «Все сотрудники», а запрос и журнал остались бы отфильтрованными по нему.
  const employeeId = selectable.some(({ employeeId: id }) => id === selectedEmployeeId) ? selectedEmployeeId : undefined;

  const params = useMemo(
    () => ({
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      call_type: callType === 'all' ? undefined : callType,
      node_filter: nodeFilter,
      employee_id: employeeId,
      offset: 0,
      limit: CALLS_PAGE_SIZE
    }),
    [dateRange, callType, nodeFilter, employeeId]
  );
  const { data, isLoading, error } = useNodeRequest({
    fetcher: fetchCalls,
    params,
    errorText: 'Не удалось загрузить звонки.'
  });
  const isPending = isLoading || (!data && !error);
  const calls = data?.items ?? [];
  const total = Number(data?.total ?? 0);

  // Сотрудник уходит срезом, а не в селект журнала: тот сбрасывает значение, которого нет в его
  // списке за период, и журнал оказался бы шире карточки.
  const journalFilters = { node_filter: nodeFilter, ...(employeeId ? { employee_ids: [employeeId] } : {}) };

  return (
    <Card data-panel-id={PANEL_IDS['ui:call-paths.calls']} className="min-w-0">
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            {panels.title(panelId)}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatCount(total)} {callsNoun(total)}
          </p>
        </div>
        <button
          type="button"
          data-ev="call_paths.calls.open_journal"
          onClick={() => onOpenCallJournal?.(journalFilters)}
          className="shrink-0 text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          Открыть в журнале ↗
        </button>
      </CardHeader>
      <CardContent>
        <label className="block max-w-sm text-xs text-muted-foreground">
          Сотрудник
          <select
            data-ev="call_paths.calls.employee_filter"
            value={employeeId ?? 'all'}
            onChange={(event) => onSelectEmployee(event.target.value)}
            className={`${SELECT_CLASS} mt-1.5 w-full`}
          >
            <option value="all">Все сотрудники</option>
            {selectable.map((employee) => (
              <option key={employee.employeeId} value={employee.employeeId}>
                {employee.employeeName} · {employee.calls}
              </option>
            ))}
          </select>
        </label>
        {error && <ErrorText>{error}</ErrorText>}
        {!error && isPending && <MutedText>Загружаем звонки…</MutedText>}
        {!error && !isPending && calls.length === 0 && <MutedText>Звонков с выбранными условиями нет.</MutedText>}
        {!error && !isPending && calls.length > 0 && (
          <div className="mt-4 max-h-[420px] divide-y overflow-y-auto">
            {calls.map((call) => (
              <button
                key={call.callId}
                type="button"
                data-ev="call_paths.calls.open_call"
                onClick={() => onOpenCallJournal?.({ ...journalFilters, callId: call.callId })}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-3 text-left text-sm hover:text-blue-600 dark:hover:text-blue-400"
              >
                {/* Формат журнала, а не настройка пояса: строка ведёт в журнал, и время должно совпасть. */}
                <span className="w-24 shrink-0 text-muted-foreground">{formatShortDate(call.callStartedAt)}</span>
                <span className="min-w-0 flex-1 font-medium">{call.employeeName || 'Без сотрудника'}</span>
                <span className="shrink-0 text-muted-foreground">{callScoreLabel(call.checklistScore)}</span>
                <ChevronRight className="h-4 w-4 shrink-0" />
              </button>
            ))}
            {total > calls.length && (
              <p className="py-3 text-xs text-muted-foreground">
                Показаны первые {calls.length}. Полный список — в журнале.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PairCallsCard;
